import { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  ArrowUp,
  ArrowDown,
  Banknote,
  ChevronsUpDown,
  Edit,
  Trash2,
  RefreshCw,
  Check,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Button,
  Badge,
} from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import TablePaginationFooter from '../TablePaginationFooter';
import { ClinicEmrBadge } from '../clinic/ClinicEmrBadge';
import { useStore } from '../../store';
import type {
  AgencyClinicResponse,
  AgencyInsuranceResponse,
  PayorEnrollmentStatus,
} from '../../services/clinicsApi';
import { getAgencyClinicDisplayName } from '../../utils/agencyClinicDisplayName';
import { shouldShowClinicEmrPills } from '../../utils/clinicEmr';

interface Props {
  insurances: AgencyInsuranceResponse[];
  /** Live clinic roster — used to resolve name via agency_clinic_id. */
  clinics: AgencyClinicResponse[];
  /** Active clinic in the parent filter — drives enrollment refresh scope. */
  selectedClinicId?: string | null;
  isRogerEmrEnabled: boolean;
  onEdit: (insurance: AgencyInsuranceResponse) => void;
  onDelete: (insuranceId: string) => void;
  pageSizeOptions?: number[];
  // Gates the new WellSky sync badge behind the edit-insurance-billing feature
  // flag. Defaults false so the badge stays dark unless the flag is enabled.
  showWellSkyBadge?: boolean;
  /** Override agency gate for EMR pills (tests / forced hide). */
  showEmrPills?: boolean;
  showEnrollmentColumns?: boolean;
  onRefreshEnrollments?: (agencyClinicId: string) => void | Promise<void>;
  refreshingClinicId?: string | null;
  // Direct per-row entry into the insurance rates editor. Only rendered when
  // provided (the billing-view feature flag gates it at the call site).
  onEditRates?: (insurance: AgencyInsuranceResponse) => void;
}

const ENROLLMENT_STATUS_LABELS: Record<PayorEnrollmentStatus, string> = {
  not_required: 'Not required',
  in_progress: 'In progress',
  failed: 'Failed',
  complete: 'Complete',
};

/** Sort rank so Failed → In progress → Not required → Complete; blank last. */
const ENROLLMENT_SORT_RANK: Record<PayorEnrollmentStatus, number> = {
  failed: 1,
  in_progress: 2,
  not_required: 3,
  complete: 4,
};

function enrollmentSortValue(
  supported: boolean | null | undefined,
  status: PayorEnrollmentStatus | null | undefined
): number {
  if (!supported || !status) return 5;
  return ENROLLMENT_SORT_RANK[status] ?? 5;
}

const CenteredCheck = ({ on }: { on: boolean }) =>
  on ? (
    <Check className='h-4 w-4 text-green-600 mx-auto' />
  ) : (
    <div className='mx-auto h-4 w-4' />
  );

const EnrollmentStatusCell = ({
  supported,
  status,
}: {
  supported?: boolean | null;
  status?: PayorEnrollmentStatus | null;
}) => {
  if (!supported || !status) {
    return <span className='text-muted-foreground'>—</span>;
  }

  return (
    <Badge
      variant={status === 'not_required' ? 'secondary' : 'outline'}
      className='font-normal'
    >
      {ENROLLMENT_STATUS_LABELS[status]}
    </Badge>
  );
};

const PayerIdCell = ({ row }: { row: AgencyInsuranceResponse }) => {
  if (row.payor_id && row.mapped_electronic_payor_id) {
    return (
      <div className='flex items-center gap-2'>
        <Check className='h-4 w-4 shrink-0 text-green-600' />
        <Badge variant='secondary'>{row.mapped_electronic_payor_id}</Badge>
      </div>
    );
  }

  if (row.edi_payer_id) {
    return <Badge variant='outline'>{row.edi_payer_id}</Badge>;
  }

  return <span className='text-muted-foreground'>—</span>;
};

type TableSectionProps = {
  insurances: AgencyInsuranceResponse[];
  clinicsById: Map<string, AgencyClinicResponse>;
  totalClinicCount: number;
  isRogerEmrEnabled: boolean;
  onEdit: (insurance: AgencyInsuranceResponse) => void;
  onDelete: (insuranceId: string) => void;
  pageSizeOptions: number[];
  showWellSkyBadge: boolean;
  showEmrPills: boolean;
  showEnrollmentColumns: boolean;
  onEditRates?: (insurance: AgencyInsuranceResponse) => void;
  showPagination: boolean;
};

function InsuranceTableSection({
  insurances,
  clinicsById,
  totalClinicCount,
  isRogerEmrEnabled,
  onEdit,
  onDelete,
  pageSizeOptions,
  showWellSkyBadge,
  showEmrPills,
  showEnrollmentColumns,
  onEditRates,
  showPagination,
}: TableSectionProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);

  const columns = useMemo<ColumnDef<AgencyInsuranceResponse>[]>(() => {
    const resolveClinicLabel = (insurance: AgencyInsuranceResponse): string => {
      const clinicId = insurance.agency_clinic_id;
      if (clinicId) {
        const clinic = clinicsById.get(clinicId);
        if (clinic) {
          return getAgencyClinicDisplayName(clinic, { totalClinicCount });
        }
      }
      // Fallback for orphan FKs or rows loaded before clinics finish fetching.
      return insurance.clinic_name?.trim() || '—';
    };

    const cols: ColumnDef<AgencyInsuranceResponse>[] = [
      {
        accessorKey: 'name',
        header: 'Insurance Name',
        cell: ({ row }) => (
          <div className='flex items-center gap-2 font-medium'>
            <span>{row.original.name}</span>
            {showWellSkyBadge && row.original.clinic_key && (
              <Badge
                variant='outline'
                className='gap-1 text-xs font-normal text-muted-foreground'
              >
                <RefreshCw className='h-3 w-3' />
                WellSky
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'payer_id',
        header: 'Payer ID',
        accessorFn: row =>
          row.mapped_electronic_payor_id ?? row.edi_payer_id ?? '',
        cell: ({ row }) => <PayerIdCell row={row.original} />,
      },
      {
        accessorKey: 'edi_clearinghouse',
        header: 'Clearinghouse',
        cell: ({ row }) =>
          row.original.edi_clearinghouse ? (
            <Badge variant='outline'>{row.original.edi_clearinghouse}</Badge>
          ) : (
            <span className='text-muted-foreground'>—</span>
          ),
      },
    ];

    // Billing enrollment view groups by clinic heading — skip the per-row column.
    if (!showEnrollmentColumns) {
      cols.push({
        id: 'clinic',
        header: 'Clinic',
        accessorFn: row => resolveClinicLabel(row),
        cell: ({ row }) => {
          const clinicId = row.original.agency_clinic_id;
          const clinic = clinicId ? clinicsById.get(clinicId) : undefined;
          const label = resolveClinicLabel(row.original);
          return (
            <span className='flex min-w-0 items-center gap-1.5'>
              <span className='truncate'>{label}</span>
              {showEmrPills && clinic && <ClinicEmrBadge clinic={clinic} />}
            </span>
          );
        },
      });
    }

    cols.push(
      {
        accessorKey: 'insurance_key',
        header: 'Insurance Key',
        cell: ({ row }) => (
          <span className='text-sm text-muted-foreground'>
            {row.original.insurance_key || '—'}
          </span>
        ),
      },
      {
        id: 'prior_auth',
        header: 'Prior Auth Required',
        accessorFn: row => !!row.visit_authorization_required,
        cell: ({ row }) => (
          <CenteredCheck on={!!row.original.visit_authorization_required} />
        ),
      },
      {
        id: 'noa_required',
        header: 'NOA Required',
        accessorFn: row => !!row.noa_required,
        cell: ({ row }) => <CenteredCheck on={!!row.original.noa_required} />,
      }
    );

    if (showEnrollmentColumns) {
      cols.push(
        {
          id: 'eligibility_supported',
          header: 'Eligibility Support',
          accessorFn: row => !!row.stedi_eligibility_enabled,
          cell: ({ row }) => (
            <CenteredCheck on={!!row.original.stedi_eligibility_enabled} />
          ),
        },
        {
          id: 'eligibility_enrollment',
          header: 'Eligibility Enrollment',
          accessorFn: row =>
            enrollmentSortValue(
              row.stedi_eligibility_enabled,
              row.eligibility_enrollment_status
            ),
          cell: ({ row }) => (
            <EnrollmentStatusCell
              supported={row.original.stedi_eligibility_enabled}
              status={row.original.eligibility_enrollment_status}
            />
          ),
        },
        {
          id: 'claims_supported',
          header: 'Claims Support',
          accessorFn: row => !!row.institutional_claim_enabled,
          cell: ({ row }) => (
            <CenteredCheck on={!!row.original.institutional_claim_enabled} />
          ),
        },
        {
          id: 'claims_enrollment',
          header: 'Claims Enrollment',
          accessorFn: row =>
            enrollmentSortValue(
              row.institutional_claim_enabled,
              row.institutional_claim_enrollment_status
            ),
          cell: ({ row }) => (
            <EnrollmentStatusCell
              supported={row.original.institutional_claim_enabled}
              status={row.original.institutional_claim_enrollment_status}
            />
          ),
        },
        {
          id: 'era_supported',
          header: 'ERA',
          accessorFn: row => !!row.era_enabled,
          cell: ({ row }) => <CenteredCheck on={!!row.original.era_enabled} />,
        },
        {
          id: 'era_enrollment',
          header: 'ERA Enrollment',
          accessorFn: row =>
            enrollmentSortValue(row.era_enabled, row.era_enrollment_status),
          cell: ({ row }) => (
            <EnrollmentStatusCell
              supported={row.original.era_enabled}
              status={row.original.era_enrollment_status}
            />
          ),
        }
      );
    } else {
      cols.push({
        id: 'eligibility',
        header: 'Eligibility Enabled',
        accessorFn: row => !!row.stedi_eligibility_enabled,
        cell: ({ row }) => (
          <CenteredCheck on={!!row.original.stedi_eligibility_enabled} />
        ),
      });
    }

    if (isRogerEmrEnabled) {
      cols.push({
        id: '__actions__',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex gap-1'>
            <Button
              size='sm'
              variant='outline'
              onClick={() => onEdit(row.original)}
            >
              <Edit className='h-4 w-4' />
            </Button>
            {onEditRates && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => onEditRates(row.original)}
                title='Billing rates'
                aria-label='Billing rates'
              >
                <Banknote className='h-4 w-4' />
              </Button>
            )}
            {/* Delete only for Roger-EMR insurances; synced rows are managed
                by the WellSky sync. */}
            {!row.original.clinic_key && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => onDelete(row.original.id)}
                className='text-red-600 hover:text-red-700 hover:bg-red-50'
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [
    isRogerEmrEnabled,
    onEdit,
    onDelete,
    onEditRates,
    showEnrollmentColumns,
    showWellSkyBadge,
    showEmrPills,
    clinicsById,
    totalClinicCount,
  ]);

  const table = useReactTable<AgencyInsuranceResponse>({
    data: insurances,
    columns,
    getRowId: row => row.id,
    state: { sorting, pagination: { pageIndex, pageSize } },
    onSortingChange: updater => {
      setSorting(prev =>
        typeof updater === 'function' ? updater(prev) : updater
      );
      setPageIndex(0);
    },
    onPaginationChange: updater => {
      const prev = { pageIndex, pageSize };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize);
        setPageIndex(0);
      } else {
        setPageIndex(next.pageIndex);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Reset to the first page whenever the (externally filtered) row set changes.
  const dataKey = insurances.map(i => i.id).join(',');
  useEffect(() => {
    setPageIndex(0);
  }, [dataKey]);

  const pageCount = table.getPageCount();
  // Clamp the page if the filtered set shrank below the current page.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex > pageCount - 1)
      setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  const pageRows = table.getRowModel().rows;
  const colSpan = columns.length;

  return (
    <div className='flex flex-col gap-3'>
      <div className='overflow-x-auto rounded-md border border-border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(hg => (
              <TableRow key={hg.id}>
                {hg.headers.map(header => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type='button'
                          className='flex items-center font-medium hover:text-foreground'
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sorted === 'asc' ? (
                            <ArrowUp className='ml-1 h-3 w-3' />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className='ml-1 h-3 w-3' />
                          ) : (
                            <ChevronsUpDown className='ml-1 h-3 w-3 opacity-40' />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className='py-10 text-center text-muted-foreground'
                >
                  No insurances match your filters
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <TablePaginationFooter
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={pageCount}
          totalCount={insurances.length}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPageSizeChange={size => table.setPageSize(size)}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
        />
      )}
    </div>
  );
}

export function InsuranceTable({
  insurances,
  clinics,
  selectedClinicId = null,
  isRogerEmrEnabled,
  onEdit,
  onDelete,
  pageSizeOptions = [10, 25, 50],
  showWellSkyBadge = false,
  showEmrPills: showEmrPillsProp,
  showEnrollmentColumns = false,
  onRefreshEnrollments,
  refreshingClinicId,
  onEditRates,
}: Props) {
  const agency = useStore(useShallow(state => state.user?.agency));
  const showEmrPills =
    showEmrPillsProp ?? shouldShowClinicEmrPills(agency ?? null);

  const clinicsById = useMemo(() => {
    const map = new Map<string, AgencyClinicResponse>();
    for (const clinic of clinics) {
      map.set(clinic.id, clinic);
    }
    return map;
  }, [clinics]);

  const isRefreshing =
    !!selectedClinicId && refreshingClinicId === selectedClinicId;

  const tableSection = (
    <InsuranceTableSection
      clinicsById={clinicsById}
      totalClinicCount={clinics.length}
      isRogerEmrEnabled={isRogerEmrEnabled}
      onEdit={onEdit}
      onDelete={onDelete}
      pageSizeOptions={pageSizeOptions}
      showWellSkyBadge={showWellSkyBadge}
      showEmrPills={showEmrPills}
      showEnrollmentColumns={showEnrollmentColumns}
      onEditRates={onEditRates}
      insurances={insurances}
      showPagination
    />
  );

  // Billing enrollment view: the parent ClinicSelect already scopes to one
  // clinic, so we only add a refresh action for that clinic here.
  if (showEnrollmentColumns && onRefreshEnrollments && selectedClinicId) {
    return (
      <section className='flex flex-col gap-3'>
        <div className='flex justify-end'>
          <Button
            size='sm'
            variant='outline'
            disabled={isRefreshing}
            onClick={() => onRefreshEnrollments(selectedClinicId)}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4${isRefreshing ? ' animate-spin' : ''}`}
            />
            {isRefreshing ? 'Refreshing…' : 'Refresh enrollment status'}
          </Button>
        </div>
        {tableSection}
      </section>
    );
  }

  return tableSection;
}

export default InsuranceTable;
